<p-dialog
    [header]="'auth.authenticator_setup' | translate"
    [visible]="visible()"
    (visibleChange)="visibleChange.emit($event)"
    [modal]="true"
    [style]="{width: '550px'}"
    [draggable]="false"
    [resizable]="false"
    (onShow)="onShow()">

    <div class="flex flex-col gap-6 py-4">
        <!-- Step Indicator -->
        @if (currentStep() !== 'loading' && currentStep() !== 'error') {
            <div class="flex justify-center mb-4">
                <div class="flex items-center gap-6">
                    <div class="flex items-center gap-2">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
                             [class.bg-blue-600]="activeStepIndex() >= 0"
                             [class.text-white]="activeStepIndex() >= 0"
                             [class.bg-gray-200]="activeStepIndex() < 0">
                            1
                        </div>
                        <span class="text-sm" [class.text-blue-600]="activeStepIndex() >= 0">{{ 'auth.step_scan' | translate }}</span>
                    </div>
                    <div class="w-8 h-px bg-gray-300"></div>
                    <div class="flex items-center gap-2">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
                             [class.bg-blue-600]="activeStepIndex() >= 1"
                             [class.text-white]="activeStepIndex() >= 1"
                             [class.bg-gray-200]="activeStepIndex() < 1">
                            2
                        </div>
                        <span class="text-sm" [class.text-blue-600]="activeStepIndex() >= 1">{{ 'auth.step_verify' | translate }}</span>
                    </div>
                    <div class="w-8 h-px bg-gray-300"></div>
                    <div class="flex items-center gap-2">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
                             [class.bg-green-600]="activeStepIndex() >= 2"
                             [class.text-white]="activeStepIndex() >= 2"
                             [class.bg-gray-200]="activeStepIndex() < 2">
                            <i class="pi pi-check" *ngIf="activeStepIndex() >= 2; else stepThreeNumber"></i>
                            <ng-template #stepThreeNumber>3</ng-template>
                        </div>
                        <span class="text-sm" [class.text-green-600]="activeStepIndex() >= 2">{{ 'auth.step_done' | translate }}</span>
                    </div>
                </div>
            </div>
        }

        <!-- Loading State -->
        @if (currentStep() === 'loading') {
            <div class="flex flex-col items-center gap-6 py-8">
                <p-progressSpinner strokeWidth="4"></p-progressSpinner>
                <p class="text-gray-600">{{ 'auth.generating_qr' | translate }}</p>
            </div>
        }

        <!-- Scan QR Code Step -->
        @if (currentStep() === 'scan') {
            <div class="flex flex-col items-center gap-6">
                <h3 class="text-lg font-semibold">{{ 'auth.scan_qr_title' | translate }}</h3>
                <p class="text-gray-600 text-sm text-center">
                    {{ 'auth.scan_qr_instruction' | translate }}
                </p>

                <!-- QR Code -->
                @if (safeQrCodeUrl()) {
                    <div class="p-4 bg-(--stela-surface) rounded-lg shadow-inner border">
                        <img [src]="safeQrCodeUrl()" alt="QR Code" class="w-48 h-48" />
                    </div>
                }

                <!-- Manual entry option -->
                <div class="w-full mt-4">
                    <button
                        type="button"
                        class="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        (click)="toggleSecretVisibility()">
                        <i class="pi" [class.pi-eye]="!showSecret()" [class.pi-eye-slash]="showSecret()"></i>
                        {{ showSecret() ? ('auth.hide_secret' | translate) : ('auth.show_secret' | translate) }}
                    </button>

                    @if (showSecret() && secretKey()) {
                        <div class="mt-2 p-3 bg-gray-100 rounded-lg">
                            <div class="flex items-center justify-between">
                                <code class="text-sm font-mono tracking-wider">{{ formattedSecret() }}</code>
                                <p-button
                                    icon="pi pi-copy"
                                    severity="secondary"
                                    [rounded]="true"
                                    size="small"
                                    (onClick)="copySecret()"
                                    [pTooltip]="'general.copy' | translate">
                                </p-button>
                            </div>
                            <p class="text-xs text-gray-500 mt-2">
                                {{ 'auth.manual_entry_help' | translate }}
                            </p>
                        </div>
                    }
                </div>

                <!-- App suggestions -->
                <div class="w-full p-3 bg-blue-50 rounded-lg text-sm">
                    <p class="font-medium text-blue-800 mb-1">{{ 'auth.recommended_apps' | translate }}</p>
                    <ul class="text-blue-700 list-disc list-inside">
                        <li>Google Authenticator</li>
                        <li>Microsoft Authenticator</li>
                        <li>Authy</li>
                        <li>1Password</li>
                    </ul>
                </div>
            </div>
        }

        <!-- Verify Code Step -->
        @if (currentStep() === 'verify') {
            <div class="flex flex-col items-center gap-6">
                <h3 class="text-lg font-semibold">{{ 'auth.verify_code_title' | translate }}</h3>
                <p class="text-gray-600 text-sm text-center">
                    {{ 'auth.verify_code_instruction' | translate }}
                </p>

                <!-- Code Input -->
                <div class="w-full max-w-xs">
                    <input
                        #codeInput
                        type="text"
                        pInputText
                        class="w-full text-center text-2xl font-mono tracking-[0.5em] py-3"
                        [ngModel]="verificationCode()"
                        (ngModelChange)="onCodeChange($event)"
                        placeholder="000000"
                        maxlength="6"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        autocomplete="one-time-code"
                        autocorrect="off"
                        autocapitalize="off"
                        spellcheck="false" />
                </div>

                <!-- Error Message -->
                @if (errorMessage()) {
                    <p-message severity="error" [text]="errorMessage()!" styleClass="w-full"></p-message>
                }

                <!-- Loading indicator -->
                @if (isLoading()) {
                    <div class="flex items-center gap-2 text-gray-600">
                        <p-progressSpinner strokeWidth="4" [style]="{width: '20px', height: '20px'}"></p-progressSpinner>
                        <span>{{ 'auth.verifying' | translate }}</span>
                    </div>
                }
            </div>
        }

        <!-- Success Step -->
        @if (currentStep() === 'success') {
            <div class="flex flex-col items-center gap-6 py-4">
                <div class="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                    <i class="pi pi-check text-4xl text-green-600"></i>
                </div>
                <h3 class="text-lg font-semibold text-green-700">{{ 'auth.authenticator_registered' | translate }}</h3>
                <p class="text-gray-600 text-sm text-center">
                    {{ 'auth.authenticator_success' | translate }}
                </p>
            </div>
        }

        <!-- Error State -->
        @if (currentStep() === 'error') {
            <div class="flex flex-col items-center gap-6 py-4">
                <div class="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                    <i class="pi pi-times text-4xl text-red-600"></i>
                </div>
                <h3 class="text-lg font-semibold text-red-700">{{ 'auth.setup_failed' | translate }}</h3>
                <p-message severity="error" [text]="errorMessage()!" styleClass="w-full"></p-message>
            </div>
        }
    </div>

    <ng-template pTemplate="footer">
        <div class="flex justify-between w-full">
            <div>
                @if (currentStep() === 'verify') {
                    <p-button
                        [label]="'general.back' | translate"
                        icon="pi pi-arrow-left"
                        severity="secondary"
                        [text]="true"
                        (onClick)="goBackToScan()">
                    </p-button>
                }
            </div>

            <div class="flex gap-2">
                @if (currentStep() === 'error') {
                    <p-button
                        [label]="'general.retry' | translate"
                        icon="pi pi-refresh"
                        severity="secondary"
                        (onClick)="retry()">
                    </p-button>
                }

                @if (currentStep() === 'success') {
                    <p-button
                        [label]="'general.done' | translate"
                        icon="pi pi-check"
                        (onClick)="complete()">
                    </p-button>
                } @else {
                    <p-button
                        [label]="'general.cancel' | translate"
                        icon="pi pi-times"
                        severity="secondary"
                        (onClick)="close()">
                    </p-button>

                    @if (currentStep() === 'scan') {
                        <p-button
                            [label]="'general.next' | translate"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            (onClick)="proceedToVerify()">
                        </p-button>
                    }

                    @if (currentStep() === 'verify') {
                        <p-button
                            [label]="'auth.verify' | translate"
                            icon="pi pi-check"
                            [disabled]="!canVerify()"
                            [loading]="isLoading()"
                            (onClick)="verifyCode()">
                        </p-button>
                    }
                }
            </div>
        </div>
    </ng-template>
</p-dialog>
